import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Input,
  Modal,
  Segmented,
  Select,
  Spin,
  Tag,
  Tabs,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRotateRight,
  faBolt,
  faChartLine,
  faClock,
  faDownload,
  faHeart,
  faShieldHalved,
  faSignal,
  faUsers,
} from '@fortawesome/pro-solid-svg-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getFeatureAnalytics,
  getFounderTargets,
  getAnalyticsKpiDashboard,
  saveFounderTarget,
  getRetentionAnalytics,
  refreshAnalyticsKpiDashboard,
  type AnalyticsDateRange,
  type FeatureAnalyticsPayload,
  type AnalyticsKpiDashboardPayload,
  type AnalyticsKpiGroup,
  type AnalyticsKpiMetric,
  type RetentionCohort,
  type RetentionPayload,
} from '../../services/analytics';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  Legend,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import FounderSummary from './FounderSummary';
import { founderPeriodLabel, previousPeriodRange } from './founderMetrics';
import './Analytics.css';
import { useAuthStore } from '../../store/authStore';
import {
  advanceProjectionSecretBuffer,
  isProjectionTypingTarget,
  parseProjectionCommand,
  projectAnalyticsPayload,
  projectFeatureAnalyticsPayload,
  projectRetentionPayload,
} from './analyticsProjection';
import { useAnalyticsProjection } from './useAnalyticsProjection';
import {
  buildAnalyticsWorkbook,
  downloadAnalyticsWorkbook,
} from './analyticsExport';

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

type TimePeriodValue = number | 'custom';
type AnalyticsTab = 'summary' | 'overview' | 'features' | 'retention';

const GROUP_ICONS: Record<string, React.ReactNode> = {
  executive: <FontAwesomeIcon icon={faHeart} />,
  product: <FontAwesomeIcon icon={faBolt} />,
  growth: <FontAwesomeIcon icon={faUsers} />,
  monetization: <FontAwesomeIcon icon={faChartLine} />,
  trust: <FontAwesomeIcon icon={faShieldHalved} />,
};

const nf = new Intl.NumberFormat('en-US');
const compact = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return Math.abs(value) >= 10000 ? compact.format(value) : nf.format(Math.round(value));
}

function formatMetricValue(metric: AnalyticsKpiMetric): string {
  if (metric.value === null || metric.value === undefined) return '-';
  if (metric.format === 'percent') {
    return `${(metric.value * 100).toFixed(metric.value > 0 && metric.value < 0.1 ? 1 : 0)}%`;
  }
  if (metric.format === 'decimal') {
    return metric.value.toFixed(metric.value < 10 ? 1 : 0);
  }
  if (metric.format === 'hours') {
    if (metric.value < 1) return `${Math.round(metric.value * 60)}m`;
    if (metric.value < 24) return `${metric.value.toFixed(metric.value < 10 ? 1 : 0)}h`;
    return `${(metric.value / 24).toFixed(1)}d`;
  }
  if (metric.format === 'currency') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(metric.value);
  }
  return formatNumber(metric.value);
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return `${(value * 100).toFixed(value > 0 && value < 0.1 ? 1 : 0)}%`;
}

function metricRatio(metric: AnalyticsKpiMetric): string | null {
  if (metric.numerator === null || metric.denominator === null) return null;
  return `${formatNumber(metric.numerator)} / ${formatNumber(metric.denominator)}`;
}

function periodLabel(data?: AnalyticsKpiDashboardPayload): string {
  if (!data) return '';
  return `${dayjs(data.period.start).format('MMM D')} - ${dayjs(data.period.end).format('MMM D, YYYY')}`;
}

function GroupIcon({ groupId }: { groupId: string }) {
  return <span className="analytics-group-icon">{GROUP_ICONS[groupId] || <FontAwesomeIcon icon={faSignal} />}</span>;
}

function MetricTile({ metric }: { metric: AnalyticsKpiMetric }) {
  const ratioLabel = metricRatio(metric);
  return (
    <article className="analytics-kpi">
      <div className="analytics-kpi-top">
        <span className="analytics-kpi-title">{metric.label}</span>
        <Tag color="green">{metric.cadence}</Tag>
      </div>
      <div className="analytics-kpi-value">{formatMetricValue(metric)}</div>
      <div className="analytics-kpi-detail">
        {ratioLabel && <strong>{ratioLabel}</strong>}
        <span>{metric.detail}</span>
      </div>
    </article>
  );
}

function MetricGroupView({ group }: { group: AnalyticsKpiGroup }) {
  return (
    <section className="analytics-section">
      <div className="analytics-section-heading">
        <div>
          <div className="analytics-section-title">
            <GroupIcon groupId={group.id} />
            <h3>{group.title}</h3>
          </div>
          <p>{group.description}</p>
        </div>
      </div>
      <div className="analytics-kpi-grid">
        {group.metrics.map((metric) => (
          <MetricTile key={metric.id} metric={metric} />
        ))}
      </div>
    </section>
  );
}

function DataStatus({ data }: { data: AnalyticsKpiDashboardPayload }) {
  const refreshedAt = data.snapshot?.refreshedAt;
  return (
    <section className="analytics-status-grid">
      <div className="analytics-status-item analytics-status-primary">
        <span><FontAwesomeIcon icon={faHeart} /> Weekly active connected couples</span>
        <strong>{formatNumber(data.summary.weeklyActiveConnectedCouples)}</strong>
      </div>
      <div className="analytics-status-item">
        <span><FontAwesomeIcon icon={faBolt} /> Meaningful events</span>
        <strong>{formatNumber(data.summary.meaningfulEvents)}</strong>
      </div>
      <div className="analytics-status-item">
        <span><FontAwesomeIcon icon={faUsers} /> Active meaningful couples</span>
        <strong>{formatNumber(data.summary.activeMeaningfulCouples)}</strong>
      </div>
      <div className="analytics-status-item">
        <span><FontAwesomeIcon icon={faClock} /> Last refreshed</span>
        <strong className="analytics-refresh-time">
          {refreshedAt ? dayjs(refreshedAt).format('MMM D, h:mm A') : dayjs(data.generatedAt).format('MMM D, h:mm A')}
        </strong>
        {data.snapshot?.stale && <Tag color="orange">Stale snapshot</Tag>}
      </div>
    </section>
  );
}

function DashboardCharts({ data }: { data: AnalyticsKpiDashboardPayload }) {
  return (
    <section className="analytics-chart-layout">
      <div className="analytics-panel analytics-panel-wide">
        <div className="analytics-panel-header">
          <div>
            <h3>Couple Activity</h3>
            <p>Active connected couples, meaningful touchpoints, signups, and pair completions.</p>
          </div>
        </div>
        <div className="analytics-chart">
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={data.series}>
              <CartesianGrid stroke="#2a2a2a" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(value) => dayjs(value).format('MMM D')} stroke="#8f8f8f" tickLine={false} />
              <YAxis yAxisId="left" stroke="#8f8f8f" tickLine={false} width={44} />
              <YAxis yAxisId="right" orientation="right" stroke="#8f8f8f" tickLine={false} width={44} />
              <ChartTooltip
                contentStyle={{ background: '#171717', border: '1px solid #333333', borderRadius: 8 }}
                labelFormatter={(value) => dayjs(value).format('MMM D, YYYY')}
              />
              <Bar yAxisId="right" dataKey="meaningfulTouchpoints" fill="#4C8DFF" radius={[4, 4, 0, 0]} name="Touchpoints" />
              <Line yAxisId="left" type="monotone" dataKey="weeklyActiveConnectedCouples" stroke="#72E2B2" strokeWidth={3} dot={false} name="Active connected couples" />
              <Line yAxisId="left" type="monotone" dataKey="pairedCouples" stroke="#F7B955" strokeWidth={2} dot={false} name="Pairs" />
              <Line yAxisId="left" type="monotone" dataKey="signups" stroke="#E86AA8" strokeWidth={2} dot={false} name="Signups" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="analytics-panel">
        <div className="analytics-panel-header">
          <div>
            <h3>Surface Breadth</h3>
            <p>Where active couples create meaningful touchpoints.</p>
          </div>
        </div>
        <div className="analytics-chart analytics-chart-compact">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.surfaces}>
              <CartesianGrid stroke="#2a2a2a" vertical={false} />
              <XAxis dataKey="surface" stroke="#8f8f8f" tickLine={false} />
              <YAxis stroke="#8f8f8f" tickLine={false} width={34} />
              <ChartTooltip contentStyle={{ background: '#171717', border: '1px solid #333333', borderRadius: 8 }} />
              <Bar dataKey="couples" fill="#72E2B2" radius={[4, 4, 0, 0]} name="Couples" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
}

function ReliabilityArea({ data }: { data: AnalyticsKpiDashboardPayload }) {
  return (
    <section className="analytics-panel">
      <div className="analytics-panel-header">
        <div>
          <h3>Reliability Pressure</h3>
          <p>Sessions, errors, and fatal errors from daily aggregates.</p>
        </div>
      </div>
      <div className="analytics-chart analytics-chart-compact">
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={data.series}>
            <CartesianGrid stroke="#2a2a2a" vertical={false} />
            <XAxis dataKey="date" tickFormatter={(value) => dayjs(value).format('MMM D')} stroke="#8f8f8f" tickLine={false} />
            <YAxis stroke="#8f8f8f" tickLine={false} width={38} />
            <ChartTooltip
              contentStyle={{ background: '#171717', border: '1px solid #333333', borderRadius: 8 }}
              labelFormatter={(value) => dayjs(value).format('MMM D, YYYY')}
            />
            <Area type="monotone" dataKey="sessions" stroke="#4C8DFF" fill="#4C8DFF33" name="Sessions" />
            <Area type="monotone" dataKey="errors" stroke="#F76F6F" fill="#F76F6F33" name="Errors" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function AnalyticsQueryAlert({ message, error }: { message: string; error: unknown }) {
  return (
    <Alert
      type="error"
      showIcon
      message={message}
      description={(error as Error)?.message || 'The analytics service did not return data for this view.'}
    />
  );
}

const FEATURE_LINES = [
  { key: 'whispers', label: 'Whispers', color: '#72E2B2' },
  { key: 'whispersMedia', label: 'Whispers media', color: '#4C8DFF' },
  { key: 'voiceNotes', label: 'Voice notes', color: '#9B8AFB' },
  { key: 'calls', label: 'Calls', color: '#F7B955' },
  { key: 'memories', label: 'Memories', color: '#E86AA8' },
  { key: 'echos', label: 'Echos', color: '#55C8D5' },
] as const;

function FeatureUsageView({ data }: { data: FeatureAnalyticsPayload }) {
  return (
    <div className="analytics-stack">
      <section className="analytics-panel analytics-panel-wide">
        <div className="analytics-panel-header">
          <div>
            <h3>Feature usage over time</h3>
            <p>Daily feature actions from source collections and privacy-safe product events.</p>
          </div>
          <Tag color="green">Daily frequency</Tag>
        </div>
        <div className="analytics-chart">
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={data.series}>
              <CartesianGrid stroke="#2a2a2a" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(value) => dayjs(value).format('MMM D')} stroke="#8f8f8f" tickLine={false} />
              <YAxis stroke="#8f8f8f" tickLine={false} width={44} />
              <ChartTooltip
                contentStyle={{ background: '#171717', border: '1px solid #333333', borderRadius: 8 }}
                labelFormatter={(value) => dayjs(value).format('MMM D, YYYY')}
              />
              <Legend />
              {FEATURE_LINES.map((feature) => (
                <Line
                  key={feature.key}
                  type="monotone"
                  dataKey={feature.key}
                  name={feature.label}
                  stroke={feature.color}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="analytics-section">
        <div className="analytics-section-heading">
          <div>
            <h3>Feature adoption</h3>
            <p>Reach, repetition, and reliability across connected couples in the selected period.</p>
          </div>
        </div>
        <div className="analytics-data-table-wrap">
          <table className="analytics-data-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Active couples</th>
                <th>Actions</th>
                <th>Adoption</th>
                <th>Repeat rate</th>
                <th>Error rate</th>
              </tr>
            </thead>
            <tbody>
              {data.features.map((feature) => (
                <tr key={feature.feature}>
                  <th scope="row">{feature.feature}</th>
                  <td>{formatNumber(feature.activeCouples)}</td>
                  <td>{formatNumber(feature.eventCount)}</td>
                  <td>{formatPercent(feature.adoption)}</td>
                  <td>{formatPercent(feature.repeatRate)}</td>
                  <td>{formatPercent(feature.errorRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

const RETENTION_MILESTONES = [
  { key: 'd7', label: 'D7' },
  { key: 'd30', label: 'D30' },
  { key: 'd60', label: 'D60' },
  { key: 'd90', label: 'D90' },
] as const;

const RETENTION_COLUMNS: Array<{ key: keyof RetentionCohort; label: string }> = [
  { key: 'd1', label: 'D1' },
  { key: 'd7', label: 'D7' },
  { key: 'd14', label: 'D14' },
  { key: 'd30', label: 'D30' },
  { key: 'd60', label: 'D60' },
  { key: 'd90', label: 'D90' },
];

function retentionCellStyle(value: number | null | undefined): React.CSSProperties | undefined {
  if (value === null || value === undefined) return undefined;
  const alpha = Math.min(0.42, 0.08 + value * 0.34);
  return { backgroundColor: `rgba(114, 226, 178, ${alpha})` };
}

function RetentionView({ retention }: { retention: RetentionPayload }) {
  const [cohortType, setCohortType] = useState<'activated' | 'paired'>(
    retention.defaultCohortType === 'paired_couple' ? 'paired' : 'activated',
  );
  const selected = retention[cohortType] || retention;

  return (
    <div className="analytics-stack">
      <section className="analytics-section">
        <div className="analytics-section-heading">
          <div>
            <h3>Retention milestones</h3>
            <p>Meaningful activity across D1 through D90 retention checkpoints.</p>
          </div>
          <Segmented
            value={cohortType}
            onChange={(value) => setCohortType(value as 'activated' | 'paired')}
            options={[
              { value: 'activated', label: 'Activated couples' },
              { value: 'paired', label: 'Paired couples' },
            ]}
          />
        </div>
        <div className="analytics-retention-summary">
          {RETENTION_MILESTONES.map((milestone) => (
            <div className="analytics-retention-stat" key={milestone.key}>
              <span>{milestone.label} retention</span>
              <strong>{formatPercent(selected.summary[milestone.key])}</strong>
            </div>
          ))}
        </div>
      </section>

      <section className="analytics-section">
        <div className="analytics-section-heading">
          <div>
            <h3>Weekly retention cohorts</h3>
            <p>Blank cells indicate cohorts that have not matured to that checkpoint.</p>
          </div>
        </div>
        <div className="analytics-data-table-wrap">
          <table className="analytics-data-table analytics-retention-table">
            <thead>
              <tr>
                <th>Cohort week</th>
                <th>Couples</th>
                {RETENTION_COLUMNS.map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {selected.cohorts.map((cohort) => (
                <tr key={cohort.cohort}>
                  <th scope="row">{dayjs(cohort.cohort).format('MMM D, YYYY')}</th>
                  <td>{formatNumber(cohort.cohortSize)}</td>
                  {RETENTION_COLUMNS.map((column) => {
                    const value = cohort[column.key] as number | null | undefined;
                    return (
                      <td key={column.key} style={retentionCellStyle(value)}>
                        {formatPercent(value)}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {selected.cohorts.length === 0 && (
                <tr><td colSpan={8} className="analytics-empty-cell">No mature cohorts in this range.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

const Analytics: React.FC = () => {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'ADMIN';
  const [period, setPeriod] = useState<TimePeriodValue>(28);
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [activeGroupId, setActiveGroupId] = useState('executive');
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('summary');
  const [exporting, setExporting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [commandOpen, setCommandOpen] = useState(false);
  const [commandValue, setCommandValue] = useState('');
  const [commandError, setCommandError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const projection = useAnalyticsProjection(user?.id || null, isAdmin);

  const days = typeof period === 'number' ? period : 90;
  const dateRange = useMemo<AnalyticsDateRange | null>(() => (
    period === 'custom' && customRange
      ? {
          fromDate: customRange[0].format('YYYY-MM-DD'),
          toDate: customRange[1].format('YYYY-MM-DD'),
        }
      : null
  ), [customRange, period]);

  const queryKey = ['analytics-kpis', days, dateRange?.fromDate, dateRange?.toDate] as const;
  const query = useQuery({
    queryKey,
    queryFn: () => getAnalyticsKpiDashboard(days, dateRange),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
  const featureQuery = useQuery({
    queryKey: ['analytics-features', days, dateRange?.fromDate, dateRange?.toDate],
    queryFn: () => getFeatureAnalytics(days, dateRange),
    enabled: activeTab === 'features',
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
  const retentionQuery = useQuery({
    queryKey: ['analytics-retention', days, dateRange?.fromDate, dateRange?.toDate],
    queryFn: () => getRetentionAnalytics(days, dateRange),
    enabled: activeTab === 'retention',
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Founder summary: the same span just before this one, for "vs previous"
  // comparisons, and the targets stored in Core.
  const previousRange = useMemo(() => previousPeriodRange(query.data?.data), [query.data?.data]);
  const previousQuery = useQuery({
    queryKey: ['analytics-kpis-previous', previousRange?.fromDate, previousRange?.toDate],
    queryFn: () => getAnalyticsKpiDashboard(previousRange ? Math.max(1, dayjs(previousRange.toDate).diff(previousRange.fromDate, 'day') + 1) : days, previousRange),
    enabled: activeTab === 'summary' && previousRange !== null,
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
  const targetsQuery = useQuery({
    queryKey: ['analytics-founder-targets'],
    queryFn: getFounderTargets,
    enabled: activeTab === 'summary',
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const sourceData = query.data?.data;
  const data = useMemo(() => (
    sourceData && projection.ready
      ? projectAnalyticsPayload(sourceData, projection.percentage)
      : undefined
  ), [projection.percentage, projection.ready, sourceData]);
  const featureData = useMemo(() => (
    featureQuery.data?.data && projection.ready
      ? projectFeatureAnalyticsPayload(featureQuery.data.data, projection.percentage)
      : undefined
  ), [featureQuery.data?.data, projection.percentage, projection.ready]);
  const retentionData = useMemo(() => (
    retentionQuery.data?.data?.retention && projection.ready
      ? projectRetentionPayload(retentionQuery.data.data.retention, projection.percentage)
      : undefined
  ), [projection.percentage, projection.ready, retentionQuery.data?.data?.retention]);
  const previousData = useMemo(() => (
    previousQuery.data?.data && projection.ready
      ? projectAnalyticsPayload(previousQuery.data.data, projection.percentage)
      : undefined
  ), [previousQuery.data?.data, projection.percentage, projection.ready]);

  async function handleSaveTarget(metricId: string, target: number, dueDate: string | null) {
    await saveFounderTarget(metricId, target, dueDate);
    await queryClient.invalidateQueries({ queryKey: ['analytics-founder-targets'] });
  }

  useEffect(() => {
    if (!isAdmin || !projection.ready) return;
    let buffer = '';

    const handleSecretPhrase = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.key.length !== 1) return;
      if (isProjectionTypingTarget(event.target)) return;

      const secret = advanceProjectionSecretBuffer(buffer, event.key);
      buffer = secret.buffer;
      if (!secret.matched) return;
      setCommandValue('');
      setCommandError(null);
      setCommandOpen(true);
    };

    window.addEventListener('keydown', handleSecretPhrase);
    return () => window.removeEventListener('keydown', handleSecretPhrase);
  }, [isAdmin, projection.ready]);
  const visibleGroups = useMemo(() => (data?.groups || [])
    .map((group) => ({
      ...group,
      title: group.title.replace(/Rituals/gi, 'Echos'),
      description: group.description.replace(/rituals/gi, 'Echos'),
      metrics: group.metrics
        .filter((metric) => metric.status === 'ready' && metric.value !== null && metric.value !== undefined)
        .map((metric) => ({ ...metric, label: metric.label.replace(/Rituals/gi, 'Echos') })),
    }))
    .filter((group) => group.metrics.length > 0), [data?.groups]);
  const activeGroup = visibleGroups.find((group) => group.id === activeGroupId) || visibleGroups[0];

  const groupOptions = useMemo(() => visibleGroups.map((group) => ({
    label: (
      <span className="analytics-segment-label">
        <GroupIcon groupId={group.id} />
        {group.title.replace(' Dashboard', '').replace(' Weekly', '')}
      </span>
    ),
    value: group.id,
  })), [visibleGroups]);

  async function handleRefresh() {
    setRefreshing(true);
    setRefreshError(null);
    const result = await refreshAnalyticsKpiDashboard(days, dateRange);
    if (result.success && result.data) {
      queryClient.setQueryData(queryKey, result);
      if (activeTab === 'features') await featureQuery.refetch();
      if (activeTab === 'retention') await retentionQuery.refetch();
      if (result.warning) setRefreshError(result.warning);
    } else {
      setRefreshError(result.error || 'Refresh failed. The previous snapshot is still displayed.');
    }
    setRefreshing(false);
  }

  async function handleExport() {
    try {
      setExporting(true);
      if (!projection.ready) throw new Error('Analytics are not ready yet.');

      const overviewResponse = sourceData ? query.data : (await query.refetch()).data;
      const featureResponse = featureQuery.data || (await featureQuery.refetch()).data;
      const retentionResponse = retentionQuery.data || (await retentionQuery.refetch()).data;
      const overviewSource = overviewResponse?.data;
      const featureSource = featureResponse?.data;
      const retentionSource = retentionResponse?.data?.retention;
      const retentionPeriod = retentionResponse?.data?.period;
      if (!overviewSource || !featureSource || !retentionSource || !retentionPeriod) {
        throw new Error('One or more analytics sections could not be loaded.');
      }

      const workbookExport = await buildAnalyticsWorkbook(
        projectAnalyticsPayload(overviewSource, projection.percentage),
        projectFeatureAnalyticsPayload(featureSource, projection.percentage),
        projectRetentionPayload(retentionSource, projection.percentage),
        retentionPeriod,
      );
      await downloadAnalyticsWorkbook(workbookExport);
      message.success('Analytics workbook downloaded');
    } catch (error) {
      message.error((error as Error)?.message || 'Failed to download analytics workbook');
    } finally {
      setExporting(false);
    }
  }

  function handleProjectionCommand() {
    const command = parseProjectionCommand(commandValue);
    if (command.type === 'invalid') {
      setCommandError(command.error);
      return;
    }
    projection.setPercentage(command.type === 'apply' ? command.percentage : null);
    setCommandError(null);
    setCommandOpen(false);
  }

  return (
    <main className="analytics-page">
      <header className="analytics-header">
        <div>
          {activeTab === 'summary' ? (
            <>
              <div className="analytics-eyebrow analytics-eyebrow-muted">Founder summary</div>
              <Title level={2}>How is Ourora doing?</Title>
              <Text className="analytics-subtitle">
                {data ? founderPeriodLabel(data) : 'Loading selected period'}
              </Text>
            </>
          ) : (
            <>
              <div className="analytics-eyebrow">
                <FontAwesomeIcon icon={faHeart} />
                Couple-centric KPI dashboard
              </div>
              <Title level={2}>Ourora KPI Dashboard</Title>
              <Text className="analytics-subtitle">
                {data ? periodLabel(data) : 'Loading selected period'}
              </Text>
            </>
          )}
        </div>
        <div className="analytics-controls">
          <Select<TimePeriodValue>
            value={period}
            onChange={(value) => setPeriod(value)}
            options={[
              { value: 28, label: 'Last 4 weeks' },
              { value: 56, label: 'Last 8 weeks' },
              { value: 30, label: 'Last 30 days' },
              { value: 60, label: 'Last 60 days' },
              { value: 90, label: 'Last 90 days' },
              { value: 'custom', label: 'Custom range' },
            ]}
          />
          {period === 'custom' && (
            <RangePicker
              value={customRange}
              onChange={(value) => setCustomRange(value as [Dayjs, Dayjs] | null)}
              allowClear={false}
            />
          )}
          <Tooltip title="Refresh KPI data">
            <Button icon={<FontAwesomeIcon icon={faArrowRotateRight} />} onClick={handleRefresh} loading={refreshing} />
          </Tooltip>
          <Tooltip title="Download all analytics as an Excel workbook">
            <Button icon={<FontAwesomeIcon icon={faDownload} />} onClick={handleExport} loading={exporting} />
          </Tooltip>
        </div>
      </header>

      {(query.isLoading || !projection.ready) && (
        <div className="analytics-loading">
          <Spin size="large" />
        </div>
      )}

      {!query.isLoading && projection.ready && (query.isError || query.data?.success === false) && (
        <Alert
          type="error"
          showIcon
          message="KPI analytics failed to load"
          description={query.data?.error || (query.error as Error)?.message || 'Check Core backend and Connect KPI endpoint health.'}
        />
      )}

      {data && refreshError && (
        <Alert
          type="warning"
          showIcon
          closable
          message="Snapshot refresh failed"
          description={refreshError}
          onClose={() => setRefreshError(null)}
        />
      )}

      {data && (
        <Tabs
          className="analytics-tabs"
          destroyOnHidden
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as AnalyticsTab)}
          items={[
            {
              key: 'summary',
              label: 'Summary',
              children: (
                <FounderSummary
                  data={data}
                  previousData={previousData}
                  targets={targetsQuery.data || []}
                  canEditTargets={isAdmin}
                  onSaveTarget={handleSaveTarget}
                  onOpenDetails={() => setActiveTab('overview')}
                />
              ),
            },
            {
              key: 'overview',
              label: 'Details',
              children: (
                <div className="analytics-stack">
                  <DataStatus data={data} />
                  <DashboardCharts data={data} />
                  <section className="analytics-group-switcher">
                    <Segmented value={activeGroup?.id} onChange={(value) => setActiveGroupId(String(value))} options={groupOptions} />
                  </section>
                  {activeGroup && <MetricGroupView group={activeGroup} />}
                  <ReliabilityArea data={data} />
                </div>
              ),
            },
            {
              key: 'features',
              label: 'Feature usage',
              children: featureQuery.isLoading
                ? <div className="analytics-loading-inline"><Spin /></div>
                : featureQuery.isError || featureQuery.data?.success === false
                  ? <AnalyticsQueryAlert message="Feature analytics failed to load" error={featureQuery.error || featureQuery.data?.error} />
                  : featureData
                    ? <FeatureUsageView data={featureData} />
                    : null,
            },
            {
              key: 'retention',
              label: 'Retention cohorts',
              children: retentionQuery.isLoading
                ? <div className="analytics-loading-inline"><Spin /></div>
                : retentionQuery.isError || retentionQuery.data?.success === false
                  ? <AnalyticsQueryAlert message="Retention analytics failed to load" error={retentionQuery.error || retentionQuery.data?.error} />
                  : retentionData
                    ? <RetentionView retention={retentionData} />
                    : null,
            },
          ]}
        />
      )}

      {commandOpen && (
        <Modal
          open
          title="Command"
          footer={null}
          width={420}
          onCancel={() => {
            setCommandError(null);
            setCommandOpen(false);
          }}
          destroyOnHidden
        >
          <div className="analytics-projection-command">
            <Input
              autoFocus
              value={commandValue}
              status={commandError ? 'error' : undefined}
              placeholder="Enter command"
              onChange={(event) => {
                setCommandValue(event.target.value);
                if (commandError) setCommandError(null);
              }}
              onPressEnter={handleProjectionCommand}
            />
            {commandError && <Text type="danger">{commandError}</Text>}
          </div>
        </Modal>
      )}
    </main>
  );
};

export default Analytics;
